import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import Loader from 'ui/atoms/Loader';
import ExchangeLayout from 'ui/layouts/ExchangeLayout';
import useWhitelabelEnvs from 'hooks/whitelabel/useWhitelabelEnvs';
import { initGoogleRecaptcha, isReCaptchaLoaded } from 'utils/googleRecaptcha';
import { useWhitelabelFeatures } from 'hooks/whitelabel/useWhitelabelFeatures';
import ExchangeSidebar from '../ExchangeSidebar';
import ExchangeRoutes from './ExchangeRoutes';
import ExchangeLoadingShell from './ExchangeLoadingShell';

/**
 * ExchangeContainer component.
 * Orchestrates the exchange layout, the reCAPTCHA bootstrap, and the route tree.
 *
 * With USE_NEW_EXCHANGE_UI on, exchange work is presented as an overlay over the still-mounted
 * routes. With the flag off, the routes are replaced by the loader, which is the behavior every
 * tenant has today; opting in is what changes it.
 * @returns {JSX.Element}
 */
const ExchangeContainer = () => {
  const isLoadingExchange = useSelector((state) => state.exchange.isLoading);
  const env = useWhitelabelEnvs();
  const features = useWhitelabelFeatures();
  const usingRecaptcha = features.EXCHANGE_REQUIRE_RECAPTCHA && env.recaptcha;
  const [isLoadingRecaptcha, setIsLoadingRecaptcha] = useState(!!usingRecaptcha);

  useEffect(() => {
    if (usingRecaptcha) {
      const isLoaded = isReCaptchaLoaded();
      if (!isLoaded) {
        const reCaptchaKey = env.recaptcha.RECAPTCHA_SITE_KEY;
        initGoogleRecaptcha({ reCaptchaKey, onLoad: () => setIsLoadingRecaptcha(false) });
      } else {
        setIsLoadingRecaptcha(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const useNewExchangeUi = Boolean(features.USE_NEW_EXCHANGE_UI);

  /**
   * Route tree, overlaid while loading when the tenant opted in and replaced by the loader when it
   * did not.
   * @returns {JSX.Element}
   */
  const renderRoutes = () => {
    if (!useNewExchangeUi) {
      return isLoadingExchange ? <Loader /> : <ExchangeRoutes />;
    }

    return (
      <ExchangeLoadingShell isLoading={Boolean(isLoadingExchange)}>
        <ExchangeRoutes />
      </ExchangeLoadingShell>
    );
  };

  return (
    <ExchangeLayout sidebar={<ExchangeSidebar />}>
      {isLoadingRecaptcha ? <Loader /> : renderRoutes()}
    </ExchangeLayout>
  );
};

export default ExchangeContainer;
